/**
 * Payer Search Dropdown
 *
 * Search input with results dropdown showing payer support levels.
 * Allows users to search for payers and initiate enrollments.
 */

import { useRef, useState } from 'react';
import { usePayerSearch } from '@features/payer-search';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PayerResponse } from '@store/generated/payers';

import {
  getClaimSubmissionSupport,
  getEnrollmentTimeframe,
  getRequiredTransactionTypes,
  getSupportLevelConfig,
  getTransactionTypeLabel,
} from '../utils/enrollmentStatus';

interface PayerSearchDropdownProps {
  /** Called when user initiates enrollment for a payer (enrolls all required transactions) */
  onEnroll: (payer: PayerResponse) => void;
  /** Whether an enrollment is currently being initiated */
  isEnrolling: boolean;
  /** ID of the payer currently being enrolled (for loading state) */
  enrollingPayerId: string | null;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Whether the search is disabled */
  disabled?: boolean;
}

export function PayerSearchDropdown({
  onEnroll,
  isEnrolling,
  enrollingPayerId,
  placeholder = 'Search for a payer to start enrollment...',
  disabled = false,
}: PayerSearchDropdownProps) {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const {
    searchTerm,
    setSearchTerm,
    payers,
    isLoading,
    isFetching,
    isSearching,
    total,
  } = usePayerSearch(300);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (searchTerm.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleEnrollClick = (payer: PayerResponse) => {
    if (payer.id) {
      onEnroll(payer);
    }
  };

  const showResults = isOpen && searchTerm.length >= 2;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          disabled={disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.paper,
            },
          }}
         slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          } }}/>

        <Popper
          open={showResults}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              maxHeight: 400,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : payers.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No payers found matching "{searchTerm}"
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Try searching by payer name or payer ID
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ px: 2, py: 1, bgcolor: theme.palette.grey[50] }}>
                  <Typography variant="caption" color="text.secondary">
                    {total} payer{total !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
                <Divider />
                <List disablePadding>
                  {payers.map((payer, index) => (
                    <PayerSearchResult
                      key={payer.stedi_id || index}
                      payer={payer}
                      onEnroll={handleEnrollClick}
                      isEnrolling={isEnrolling && enrollingPayerId === payer.id}
                      disabled={isEnrolling}
                    />
                  ))}
                </List>
              </>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

interface PayerSearchResultProps {
  payer: PayerResponse;
  onEnroll: (payer: PayerResponse) => void;
  isEnrolling: boolean;
  disabled: boolean;
}

function PayerSearchResult({ payer, onEnroll, isEnrolling, disabled }: PayerSearchResultProps) {
  const theme = useTheme();
  const requiredTypes = getRequiredTransactionTypes(payer);
  const needsEnrollment = requiredTypes.length > 0;
  const claimSupport = getClaimSubmissionSupport(payer);
  const supportLevel = needsEnrollment
    ? 'ENROLLMENT_REQUIRED'
    : claimSupport === 'SUPPORTED'
      ? 'SUPPORTED'
      : 'NOT_SUPPORTED';
  const supportConfig = getSupportLevelConfig(supportLevel);
  const timeframe = needsEnrollment ? getEnrollmentTimeframe(payer) : null;

  return (
    <ListItem
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        alignItems: 'flex-start',
      }}
    >
      <ListItemAvatar>
        <Avatar
          src={payer.avatar_url || undefined}
          alt={payer.display_name}
          sx={{ width: 40, height: 40 }}
        >
          {payer.display_name.charAt(0)}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" component="span">
              {payer.display_name}
            </Typography>
            <Chip
              label={supportConfig.label}
              size="small"
              color={supportConfig.color}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            {needsEnrollment && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                Enrollment required: {requiredTypes.map(getTransactionTypeLabel).join(', ')}
              </Typography>
            )}
            {!needsEnrollment && supportLevel === 'SUPPORTED' && (
              <Typography variant="caption" color="success.main">
                Claims can be submitted immediately
              </Typography>
            )}
            {!needsEnrollment && supportLevel === 'NOT_SUPPORTED' && (
              <Typography variant="caption" color="error.main">
                This payer doesn't accept electronic claims
              </Typography>
            )}
            {needsEnrollment && timeframe && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {timeframe}
              </Typography>
            )}
          </Box>
        }
        sx={{ flex: 1, mr: 2 }}
      />

      {needsEnrollment && (
        <>
          {payer.id ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onEnroll(payer)}
              disabled={disabled || isEnrolling}
              sx={{ alignSelf: 'center', minWidth: 80 }}
            >
              {isEnrolling ? <CircularProgress size={16} /> : 'Enroll'}
            </Button>
          ) : (
            <Tooltip title="This payer requires enrollment but isn't available yet. Contact support if you need to enroll.">
              <Box
                sx={{
                  alignSelf: 'center',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Coming Soon
                </Typography>
              </Box>
            </Tooltip>
          )}
        </>
      )}
    </ListItem>
  );
}
